<script setup lang="ts">
import type { Themeable, UIPasswordValidationFields, UIPasswordValidationFieldType } from '@core/types'
import { computed, useI18n, passwordValidation, useDefaults, useTheme } from '#imports'

export interface UIPasswordStrengthProps extends Themeable {
    password: string
    compact?: boolean
}

const COMPACT_LABELS: Record<string, string> = {
    length: '8+',
    number: '0-9',
    uppercase: 'A-Z',
    lowercase: 'a-z',
}

const _props = withDefaults(defineProps<UIPasswordStrengthProps>(), {
    color: undefined,
    compact: false,
})
const props = useDefaults({
    props: _props,
    defaults: {
        color: 'success'
    }
})
const { themeClass } = useTheme(() => {
    return {
        color: props.color
    }
})

const { t } = useI18n()

const VALIDATION_FIELD_TYPES: UIPasswordValidationFieldType[] = ['length', 'number', 'uppercase', 'lowercase']

const validationFields = computed<UIPasswordValidationFields[]>(() => {
    return VALIDATION_FIELD_TYPES.map(type => ({
        type,
        isValid: passwordValidation(props.password, type).isValid,
        message: t(`core.components.UIPasswordStrength.messages.${type}`),
    }))
})
</script>

<template>
    <div class="password-strength" :class="[themeClass, { 'password-strength--compact': props.compact }]">
        <p v-if="!props.compact" class="password-strength__title">
            {{ t('core.components.UIPasswordStrength.title') }}
        </p>

        <div class="password-strength__rules">
            <div
                v-for="{ type, isValid, message } in validationFields"
                :key="type"
                :class="[
                    'password-strength__rule',
                    { 'password-strength__rule--is-valid': isValid }
                ]"
            >
                <div class="password-strength__icon-wrapper">
                    <UIIcon
                        v-if="isValid"
                        class="password-strength__icon"
                        name="check"
                        size="9"
                        color="#fff"
                    />
                </div>

                <div class="password-strength__message">
                    {{ props.compact ? COMPACT_LABELS[type] || message : message }}
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.password-strength {
    font-size: $font-size-xs;
    font-weight: $text-regular;

    @media (min-width: $screen-xs) {
        font-size: $font-size-sm;
    }

    &__title {
        color: var(--on-background, $dialog-auth-font-color);
        opacity: 0.5;
        margin-bottom: 5px;
    }

    &__rules {
        display: grid;
        grid-template-columns: 1fr 1fr;
        flex-wrap: wrap;
        gap: 8px;

        @media (min-width: $screen-xs) {
            padding: 0 8px;
        }
    }

    &__rule {
        display: flex;
        gap: 5px;
        align-items: center;

        &--is-valid {
            color: var(--color, $check-mark-color);

            .password-strength {
                &__icon-wrapper {
                    background: var(--color, $check-mark-color);
                }

                &__message {
                    opacity: 1;
                }
            }
        }
    }

    &__icon-wrapper {
        position: relative;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: var(--surface-border, #c5c5c5);
    }

    &__icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    &__message {
        opacity: 0.5;
    }

    &--compact {
        .password-strength__rules {
            display: flex;
            justify-content: center;
            gap: 16px;
            padding: 0;
        }

        .password-strength__icon-wrapper {
            width: 14px;
            height: 14px;
            border: 1.5px solid var(--surface-border, #c5c5c5);
            background: transparent;
        }

        .password-strength__rule--is-valid .password-strength__icon-wrapper {
            border-color: var(--color, $check-mark-color);
            background: var(--color, $check-mark-color);
        }

        .password-strength__message {
            font-size: $font-size-xs;
        }
    }
}
</style>
